'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import { useUpsertPostPurchaseRule, UpsertPostPurchaseRuleInput } from '@/lib/api/postPurchaseRules'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { PostPurchaseRuleForm } from '@/components/form/post-purchase-rule/post-purchase-rule-form'
import { postPurchaseRuleFormSchema, PostPurchaseRuleLine, type PostPurchaseRuleFormData } from '@/components/form/post-purchase-rule/validation'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

/**
 * Page for creating a new post purchase rule
 */
const CreatePostPurchaseRulePageInner = () => {
	const { t } = useTranslation('postPurchaseRules')
	const { id: storeId, navigate, storeRoutes } = useStoreNavigate()
	const { toast } = useToast()
	const upsertRuleMutation = useUpsertPostPurchaseRule()
	const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

	const methods = useForm<PostPurchaseRuleFormData>({
		defaultValues: {
			ruleOperator: 'OR',
			priority: 1,
			offers: [] as string[],
			lines: [] as PostPurchaseRuleLine[],
		},
		resolver: zodResolver(postPurchaseRuleFormSchema) as any,
		mode: 'onBlur',
	})

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = methods

	const handleCreateRule = async (data: PostPurchaseRuleFormData) => {
		try {
			setFormError(null)

			const input: UpsertPostPurchaseRuleInput = {
				storeId,
				ruleOperator: data.ruleOperator,
				priority: data.priority ?? null,
				offers: data.offers,
				lines: data.lines.map((line) => {
					if (line.type === 'customer_did_not_subscribe_to_club') {
						return { type: line.type }
					}
					return {
						type: line.type,
						productId: line.productId,
						minimumQuantity: line.minimumQuantity,
					}
				}),
			}

			await upsertRuleMutation.mutateAsync(input)

			setTimeout(() => {
				navigate(storeRoutes.POST_PURCHASE())
			}, 500)
		} catch (error) {
			const parsedError = parseTRPCError(error)
			setFormError(parsedError)

			if (!parsedError.isValidationError) {
				toast({
					title: t('error.error'),
					description: parsedError.message,
					variant: 'destructive',
				})
			}
		}
	}

	return (
		<div className="bg-background min-h-screen">
			<div className="flex flex-col space-y-4 sm:space-y-6">
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(handleCreateRule)}>
						<div className="mb-4 flex items-center justify-between">
							<div className="flex items-center gap-2">
								<BackButton to={storeRoutes.POST_PURCHASE()} />
								<div>
									<h1 className="text-2xl font-semibold">{t('create.title')}</h1>
									<p className="text-muted-foreground text-sm">{t('create.description')}</p>
								</div>
							</div>
							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => navigate(storeRoutes.POST_PURCHASE())}
									disabled={isSubmitting}
								>
									{t('actions.cancel')}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting ? t('actions.creating') : t('actions.create')}
								</Button>
							</div>
						</div>

						{formError && !formError.isValidationError && (
							<div className="mb-6">
								<ErrorDisplay error={formError} />
							</div>
						)}

						<PostPurchaseRuleForm />
					</form>
				</FormProvider>
			</div>
		</div>
	)
}

export const CreatePostPurchaseRulePage = withRequiredPermission(CreatePostPurchaseRulePageInner, PERMISSIONS.STORES_MANAGE)
export default CreatePostPurchaseRulePage

